import { API_BASE } from '../config.js';
import type { Division } from '@multirx/shared/types/division.js';
import type { DivisionStandingsView } from '@multirx/shared/types/standings.js';
import type { Car } from '@multirx/shared/types/car.js';
import type { Driver } from '@multirx/shared/types/driver.js';
import type { Race, RaceParticipant } from '@multirx/shared/types/race.js';

export async function fetchDivisions(): Promise<Division[]> {
  const res = await fetch(`${API_BASE}/api/divisions`);
  if (!res.ok) throw new Error('Erreur lors du chargement des divisions');
  return res.json() as Promise<Division[]>;
}

export async function fetchCarsByDivision(divisionId: number): Promise<Car[]> {
  const res = await fetch(`${API_BASE}/api/divisions/${divisionId}/cars`);
  if (!res.ok) throw new Error('Erreur lors du chargement des voitures');
  return res.json() as Promise<Car[]>;
}

export async function fetchDriversByDivision(divisionId: number): Promise<Driver[]> {
  const res = await fetch(`${API_BASE}/api/divisions/${divisionId}/drivers`);
  if (!res.ok) throw new Error('Erreur lors du chargement des pilotes');
  return res.json() as Promise<Driver[]>;
}

export async function fetchStandingsByDivision(divisionId: number): Promise<DivisionStandingsView> {
  const res = await fetch(`${API_BASE}/api/divisions/${divisionId}/standings`);
  if (!res.ok) throw new Error('Erreur lors du chargement du classement');
  return res.json() as Promise<DivisionStandingsView>;
}

export interface SeedDatabaseResponse {
  message: string;
  skipped?: boolean;
  carsCreated?: number;
  driversCreated?: number;
  circuitsCreated?: number;
}

export async function seedDivisions(): Promise<SeedDatabaseResponse> {
  const res = await fetch(`${API_BASE}/api/divisions/seed`, { method: 'POST' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors du seed');
  }
  return res.json() as Promise<SeedDatabaseResponse>;
}

export async function createRace(params: {
  circuitId: number;
  division: number;
  numParticipants: number;
  numHumanPlayers: number;
}): Promise<Race> {
  const res = await fetch(`${API_BASE}/api/races`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors de la création de la course');
  }
  return res.json() as Promise<Race>;
}

export async function fetchRace(id: number): Promise<Race & { participants: RaceParticipant[] }> {
  const res = await fetch(`${API_BASE}/api/races/${id}`);
  if (!res.ok) {
    if (res.status === 404) throw new Error('Course non trouvée');
    throw new Error('Erreur lors du chargement de la course');
  }
  return res.json() as Promise<Race & { participants: RaceParticipant[] }>;
}

export async function addRaceParticipant(params: {
  raceId: number;
  driverId: number;
  carId: number;
  isHuman: boolean;
  socketId?: string | null;
}): Promise<void> {
  const res = await fetch(`${API_BASE}/api/races/${params.raceId}/participants`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      driverId: params.driverId,
      carId: params.carId,
      isHuman: params.isHuman,
      socketId: params.socketId ?? null,
    }),
  });
  if (!res.ok) throw new Error("Erreur lors de l'ajout du participant");
}

export async function finishRace(
  raceId: number,
  positions: { driverId: number; position: number }[]
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/races/${raceId}/finish`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ positions }),
  });
  if (!res.ok) throw new Error('Erreur lors de la finalisation de la course');
}

/**
 * Enregistre les points d'une course terminée (schéma : classement inversé × 2, +2 pour le 1er).
 */
export async function recordRaceResult(
  divisionId: number,
  results: { driverId: number; position: number }[]
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/divisions/${divisionId}/record-result`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ results }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(
      (err as { error?: string }).error ?? "Erreur lors de l'enregistrement des points"
    );
  }
}
